import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder:text-slate-500";
const labelCls = "text-[11px] uppercase tracking-widest text-slate-500 mb-1 block";

export default function WithdrawDialog({ open, onOpenChange, available, onSuccess }) {
  const [form, setForm] = useState({
    amount: "",
    paypal_email: "",
    bank_name: "",
    account_holder: "",
    account_number: "",
    ifsc_or_routing: "",
    swift: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.amount);
    if (!amount || amount <= 0) return toast.error("Enter an amount");
    if (amount > available + 0.01) return toast.error(`You can only withdraw up to $${available.toFixed(2)}`);
    if (!form.paypal_email.includes("@")) return toast.error("Enter a valid PayPal email");
    if (!form.account_holder.trim() || !form.account_number.trim() || !form.bank_name.trim() || !form.ifsc_or_routing.trim()) {
      return toast.error("Fill in every bank field");
    }
    setBusy(true);
    try {
      await api.post("/artist/withdrawals", { ...form, amount });
      toast.success("Withdrawal requested — you'll get an email once processed");
      onSuccess && onSuccess();
      onOpenChange(false);
      setForm({ amount: "", paypal_email: "", bank_name: "", account_holder: "", account_number: "", ifsc_or_routing: "", swift: "", notes: "" });
    } catch (err) {
      toast.error(formatApiError(err, "Withdrawal failed"));
    } finally {
      setBusy(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0b1220] border-white/10 text-white sm:max-w-lg" data-testid="withdraw-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Request a withdrawal</DialogTitle>
          <DialogDescription className="text-slate-400">
            Available balance: <span className="text-white">${available.toFixed(4)}</span>. Payouts are processed manually within 5–7 business days.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="withdraw-form">
          <div>
            <label className={labelCls}>Amount (GBP)</label>
            <input
              data-testid="withdraw-amount"
              type="number" step="0.01" min="0.01"
              value={form.amount}
              onChange={(e) => set("amount", e.target.value)}
              className={inputCls}
              placeholder="e.g. 50.00"
            />
          </div>
          <div>
            <label className={labelCls}>PayPal email</label>
            <input
              data-testid="withdraw-paypal"
              type="email"
              value={form.paypal_email}
              onChange={(e) => set("paypal_email", e.target.value)}
              className={inputCls}
              placeholder="you@paypal.com"
            />
          </div>
          <div className="pt-1 border-t border-white/10">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 mt-3">Bank details (alternate payout)</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Bank name</label>
                <input
                  data-testid="withdraw-bank"
                  value={form.bank_name}
                  onChange={(e) => set("bank_name", e.target.value)}
                  className={inputCls}
                  placeholder="e.g. HDFC / Chase"
                />
              </div>
              <div>
                <label className={labelCls}>Account holder name</label>
                <input
                  data-testid="withdraw-holder"
                  value={form.account_holder}
                  onChange={(e) => set("account_holder", e.target.value)}
                  className={inputCls}
                  placeholder="Full name on account"
                />
              </div>
              <div>
                <label className={labelCls}>Account number</label>
                <input
                  data-testid="withdraw-account"
                  value={form.account_number}
                  onChange={(e) => set("account_number", e.target.value)}
                  className={inputCls}
                  placeholder="Account / IBAN"
                />
              </div>
              <div>
                <label className={labelCls}>IFSC / Routing #</label>
                <input
                  data-testid="withdraw-ifsc"
                  value={form.ifsc_or_routing}
                  onChange={(e) => set("ifsc_or_routing", e.target.value)}
                  className={inputCls}
                  placeholder="e.g. HDFC0000123 / 021000021"
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>SWIFT / BIC (international only)</label>
                <input
                  value={form.swift}
                  onChange={(e) => set("swift", e.target.value)}
                  className={inputCls}
                  placeholder="Optional"
                />
              </div>
            </div>
          </div>
          <div>
            <label className={labelCls}>Notes (optional)</label>
            <textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              className={inputCls + " min-h-[60px]"}
              placeholder="Any context our finance team should know"
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-full border border-white/15 text-white text-sm px-5 py-2.5 hover:bg-white/5"
            >
              Cancel
            </button>
            <button
              data-testid="withdraw-submit"
              type="submit"
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-full bg-white text-black font-medium text-sm px-6 py-2.5 hover:bg-slate-200 disabled:opacity-60"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : "Request payout"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
